import { useCallback } from 'react';

// @ts-nocheck
import {
  FlagPayload,
  HoldMotionDependentQueueTypes,
  paramToQueueTypeMap,
  ProgramType,
  QueueDocumentResponse,
  QueueNote,
  QueueType,
  UpdateQueue,
} from 'types';

import { api } from './api';

export interface RequirementFlagSettingOption {
  id: string;
  name: string;
  type: string;
  dataKey: string;
}

interface GetQueuesResponse {
  pageNum: number;
  total: number;
  queues: Array<QueueDocumentResponse>;
  /**
   * Total with advanced filters and search term stripped. Optional — the server omits it when it
   * could not be computed, and a missing value must leave the last known count alone rather than
   * render as 0.
   */
  unfilteredTotal?: number;
}

export interface QueueFeedEvent {
  queueType: QueueType;
  action: 'created' | 'updated' | 'removed' | 'bulk';
  queueId?: string;
  accountId?: string;
  unfilteredTotal?: number;
  /**
   * On `updated` events, whether the write moved the queue's status. Absent from pods that predate
   * the field, so it must be read as "may have changed" rather than `false`.
   */
  statusChanged?: boolean;
}

export interface QueueFeedState {
  unfilteredTotal?: number;
  /** Latest event, re-identified on every message so consumers re-run even on identical payloads. */
  lastEvent?: QueueFeedEvent & { sequence: number };
}

interface HoldQueueSetting {
  queueType: QueueType;
  isEnabled: boolean;
}

interface GetQueueHoldMotionStatusResponse {
  isEnabled: boolean;
  holdQueueSettings: HoldQueueSetting[];
}

interface PostQueueHoldMotionStatusResponse {
  queueType: QueueType;
  isEnabled?: boolean;
  queueIds?: string[];
}

export interface HoldMotionProgramSetting {
  programType: ProgramType;
  enabled: boolean;
  heldCount: number;
}

interface GetHoldMotionProgramSettingsResponse {
  programSettings: HoldMotionProgramSetting[];
}

interface UpdateHoldMotionProgramSettingResponse {
  programType: ProgramType;
  enabled: boolean;
  releasedCount: number;
}

const baseHoldMotionTag = (type: QueueType) => [
  {
    type: 'Queues' as const,
    id: `hold-motion-${type}`,
  },
  { type: 'Queues' as const, id: `filters-${type}` },
];

const HOLD_MOTION_PROGRAMS_TAG = { type: 'Queues' as const, id: 'hold-motion-programs' };

// Mirrors createSSECacheHandler's reconnect backoff (see createSSECacheHandler.ts) — that helper
// isn't reused directly because it patches cache data via updateCachedData, whereas the queue feed
// is a signal-and-refetch subscription: every message just invalidates the Queues LIST tag.
const QUEUE_FEED_RECONNECT_BASE_DELAY_MS = 1_000;
const QUEUE_FEED_RECONNECT_MAX_DELAY_MS = 30_000;
export const QUEUE_FEED_INVALIDATE_DEBOUNCE_MS = 1_500;
// Every connected browser receives the same SSE push at nearly the same instant, so a fixed debounce
// would make them all refetch in lockstep and spike the API. A random offset spreads the refetches
// across a window so the load is smeared instead of synchronized.
export const QUEUE_FEED_INVALIDATE_JITTER_MS = 3_000;
// Ceiling on the coalescing window. Without it, messages arriving faster than the debounce interval
// reset the timer forever and the list never refreshes — the worst case being peak load, when every
// queue update fires a message. The cap lets a burst delay the refresh but never cancel it outright.
const QUEUE_FEED_INVALIDATE_MAX_WAIT_MS = 5_000;

export interface QueueFeedPatchPlan {
  /** Re-read the row by id and merge it into the cached page. */
  patchRow: boolean;
  /** Remove the row from the cached page immediately, ahead of the refetch settling it. */
  dropRow: boolean;
  /** Ask the server to re-resolve the page — the only thing that can decide membership. */
  refetch: boolean;
}

/**
 * Decides what a single queue feed event is worth doing, given whether the row it names is currently
 * on the reviewer's page. Pure so the decision table can be tested directly; `Queue.tsx` owns the
 * dispatching and the debounce.
 *
 * The reviewer's advanced filters run in Atlas Search, so the client can never decide membership
 * itself — every branch that might change *which* rows belong defers to a refetch.
 */
export const planQueueFeedPatch = (event: QueueFeedEvent, isVisible: boolean): QueueFeedPatchPlan => {
  const plan: QueueFeedPatchPlan = { patchRow: false, dropRow: false, refetch: false };

  // `bulk` carries no id by design — the publisher never learned which rows it touched.
  if (event.action === 'bulk' || !event.queueId) {
    return { ...plan, refetch: true };
  }

  // A new row changes both membership and page composition under $skip/$limit, so there is nothing
  // useful to patch — only the server knows where it lands.
  if (event.action === 'created') {
    return { ...plan, refetch: true };
  }

  // Drop a visible row immediately so the reviewer stops seeing work that no longer exists, then let
  // the refetch settle the page. Atlas Search is eventually consistent, so an immediate refetch can
  // still return the row — the caller's debounce covers that lag as well as coalescing bursts.
  if (event.action === 'removed') {
    return { ...plan, dropRow: isVisible, refetch: true };
  }

  // `updated`. Off-screen rows are the overwhelming majority of events, and a non-status write to one
  // cannot change what is on screen — skipping those is where the refetch reduction actually comes
  // from. Any count they changed already arrived on the event itself.
  if (!isVisible) {
    // A status change is the exception: it can pull a row the reviewer's filter previously excluded
    // *into* the view — a resubmit, a release from hold — and only the server can evaluate that
    // predicate. Absent on pods that predate the field, which has to read as "may have changed".
    return { ...plan, refetch: event.statusChanged !== false };
  }

  // Patch repaints the row immediately; the refetch confirms it still belongs in the filtered view.
  return { ...plan, patchRow: true, refetch: true };
};

/**
 * A 409 from `updateQueue` means the queue was claimed by someone else between our read and our
 * write. RTK Query doesn't run `invalidatesTags` for a rejected mutation, so refresh the list here
 * to flip the row from a stale "Claim" affordance to its real assignee — independent of the
 * live-feed flag. Exported for direct unit testing (the relative `/api` base URL can't be exercised
 * through the store in the test env).
 */
export const invalidateQueuesListOnClaimConflict = (err: unknown, dispatch: (action: unknown) => void): void => {
  if ((err as { error?: { status?: number } })?.error?.status === 409) {
    dispatch(queuesApi.util.invalidateTags([{ type: 'Queues', id: 'LIST' }]));
  }
};

export const queuesApi: any = api.injectEndpoints({
  endpoints: (build) => ({
    getQueues: build.query<GetQueuesResponse, Record<any, any>>({
      query: (queryParams) => {
        const params = new URLSearchParams();

        Object.entries(queryParams || {}).forEach(([key, value]) => {
          if (value === undefined || value === null) {
            return;
          }

          if (Array.isArray(value)) {
            value.forEach((item) => {
              params.append(key, String(item));
            });
            return;
          }

          params.append(key, String(value));
        });

        return {
          url: `queues${params.toString() ? `?${params.toString()}` : ''}`,
        };
      },
      transformResponse: (response: any) => {
        const { data, pageNum, total, unfilteredTotal } = response || {};
        return {
          pageNum,
          total,
          queues: data,
          unfilteredTotal,
        };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.queues.map(({ id }) => ({ type: 'Queues' as const, id: String(id) })),
              { type: 'Queues', id: 'LIST' },
            ]
          : [{ type: 'Queues', id: 'LIST' }],
    }),
    getQueueListFilters: build.query<any, Record<any, any>>({
      query: ({ type, includeOptions }) => {
        const params = new URLSearchParams({ type });
        if (includeOptions !== undefined) {
          params.set('includeOptions', String(includeOptions));
        }
        return {
          url: `queues/filters?${params.toString()}`,
        };
      },
      providesTags: (_result, _error, arg) => [
        { type: 'Queues', id: `filters-${paramToQueueTypeMap[arg.type]?.type ?? arg.type}` },
      ],
    }),
    getQueueListFilter: build.query<any, { id: string; type: string }>({
      query: ({ id, type }) => ({
        url: `queues/filters/${id}?type=${type}`,
      }),
      providesTags: (_result, _error, arg) => [
        { type: 'Queues', id: `filter-${paramToQueueTypeMap[arg.type]?.type ?? arg.type}-${arg.id}` },
      ],
    }),
    updateQueue: build.mutation<QueueDocumentResponse, { data: UpdateQueue; id: string }>({
      query: ({ data, id }) => ({
        url: `queues/${id}`,
        method: 'PATCH',
        body: data,
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          invalidateQueuesListOnClaimConflict(err, dispatch);
        }
      },
      invalidatesTags: (result, _error, arg) => [
        { type: 'Queues', id: String(result?.id) },
        { type: 'Tasks', id: 'LIST' },
        { type: 'Accounts', id: String(result?.account?.id) },
        { type: 'AccountMilestones', id: 'AccountMilestones' },
        { type: 'AccountProgress', id: 'AccountProgress' },
        { type: 'Applications', id: 'LIST' },
        { type: 'Queues', accountId: String(result?.account?.id) },
        { type: 'DocumentDocument', id: 'LIST' },
        { type: 'AccountMilestones', id: String(result?.account?.id) },
        { type: 'AccountStipulations', id: 'LIST' },
        ...(arg.data.assigneeId !== undefined ? [{ type: 'Checklists' as const, id: `queue-${arg.id}` }] : []),
      ],
    }),
    addNoteToQueue: build.mutation<QueueDocumentResponse, { data: Pick<QueueNote, 'note'>; id: string }>({
      query: ({ data, id }) => ({
        url: `queues/${id}/notes`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: 'Queues', id: arg?.id }],
    }),
    getQueueByAccountIdAndType: build.query<QueueDocumentResponse, { accountId: string; type: string }>({
      query: ({ accountId, type }) => `accounts/${accountId}/queues/${type}`,
      providesTags: (result, _error, arg) => [
        { type: 'Queues', accountId: arg?.accountId },
        { type: 'Queues', id: String(result?.id) },
      ],
    }),
    getQueuesByAccountId: build.query<QueueDocumentResponse[], { accountId: string }>({
      query: ({ accountId }) => `accounts/${accountId}/queues`,
      providesTags: (result, _error, arg) => [
        { type: 'Queues', accountId: arg?.accountId },
        ...(result?.map((q) => ({ type: 'Queues' as const, id: String(q?.id) })) ?? []),
      ],
    }),
    getQueuesByAccountIdAndDocumentType: build.query<QueueDocumentResponse, { accountId: string; type: string }>({
      query: ({ accountId, type }) => `accounts/${accountId}/document/${type}/queues`,
      providesTags: (result, _error, arg) => [
        { type: 'Queues', accountId: arg?.accountId },
        { type: 'Queues', id: String(result?.id) },
      ],
    }),
    getQueueById: build.query<QueueDocumentResponse, { id: string }>({
      query: ({ id }) => `queues/${id}`,
      providesTags: (result) => [{ type: 'Queues', id: String(result?.id) }],
    }),
    addFlagToQueue: build.mutation<QueueDocumentResponse, { id: string; flag: FlagPayload }>({
      query: ({ id, flag }) => ({
        url: `queues/${id}/flags`,
        method: 'POST',
        body: flag,
      }),
      invalidatesTags: (result) => [{ type: 'Queues', id: String(result?.id) }],
    }),
    updateFlag: build.mutation<QueueDocumentResponse, { id: string; flagId: string; flag: FlagPayload }>({
      query: ({ id, flagId, flag }) => ({
        url: `queues/${id}/flags/${flagId}`,
        method: 'PATCH',
        body: flag,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Queues', id }],
    }),
    resolveFlag: build.mutation<
      QueueDocumentResponse,
      { id: string; dataKey: string; fileName?: string; status?: string; flagId?: string }
    >({
      query: ({ id, dataKey, fileName, status, flagId }) => ({
        url: `queues/${id}/flags/${dataKey}${
          (fileName || status || flagId) &&
          '?' +
            new URLSearchParams({
              ...(fileName ? { fileName } : {}),
              ...(status ? { status } : {}),
              ...(flagId ? { flagId } : {}),
            }).toString()
        }`,
        method: 'DELETE',
      }),
      invalidatesTags: (result) => [{ type: 'Queues', id: String(result?.id) }],
    }),
    getQueueHoldMotionStatus: build.query<GetQueueHoldMotionStatusResponse, { type: QueueType }>({
      query: ({ type }) => `queues/${type}/hold-queue-settings`,
      providesTags: (_result, _error, arg) => [{ type: 'Queues', id: `hold-motion-${arg.type}` }],
    }),
    getRequirementFlagSettingsForQueue: build.query<
      RequirementFlagSettingOption[],
      { queueId: string; search: string }
    >({
      query: ({ queueId, search }) => ({
        url: `/queues/${queueId}/requirement-flag-settings${search ? `?search=${encodeURIComponent(search)}` : ''}`,
      }),
    }),
    checkQueuesExportLimit: build.query<any, string>({
      query: (queryParams) => ({
        url: `queues/export/csv/precheck${queryParams}`,
      }),
    }),
    createQueuesExportRequest: build.mutation<any, string>({
      query: (queryParams) => ({
        url: `queues/export/csv${queryParams}`,
        method: 'POST',
      }),
    }),
    updateHoldMotionSettings: build.mutation<
      PostQueueHoldMotionStatusResponse,
      { type: QueueType; isEnabled?: boolean; filters?: string; queueIds?: string[]; releaseAll?: boolean }
    >({
      query: ({ type, isEnabled, filters, queueIds, releaseAll }) => ({
        url: `queues/hold-queue-settings`,
        method: 'POST',
        body: { isEnabled, queueType: type, advancedFilters: filters, queueIds, releaseAll },
      }),
      invalidatesTags: (_result, _error, arg) => {
        if (arg.type === QueueType.holdMotion) {
          return [
            ...baseHoldMotionTag(QueueType.holdMotion),
            ...HoldMotionDependentQueueTypes.flatMap(baseHoldMotionTag),
          ];
        }

        return [...baseHoldMotionTag(arg.type), ...baseHoldMotionTag(QueueType.holdMotion)];
      },
    }),
    getHoldMotionProgramSettings: build.query<GetHoldMotionProgramSettingsResponse, void>({
      query: () => `queues/hold-motion-program-settings`,
      providesTags: [HOLD_MOTION_PROGRAMS_TAG],
    }),
    updateHoldMotionProgramSetting: build.mutation<
      UpdateHoldMotionProgramSettingResponse,
      { programType: ProgramType; enabled: boolean }
    >({
      query: ({ programType, enabled }) => ({
        url: `queues/hold-motion-program-settings`,
        method: 'POST',
        body: { programType, enabled },
      }),
      invalidatesTags: [
        HOLD_MOTION_PROGRAMS_TAG,
        { type: 'Queues', id: 'LIST' },
        ...baseHoldMotionTag(QueueType.holdMotion),
        ...HoldMotionDependentQueueTypes.flatMap(baseHoldMotionTag),
      ],
    }),
    // Owns an SSE subscription lifecycle only — no real data. Every message coalesces into a single
    // debounced invalidate of the Queues LIST tag so the active list + count refetch (signal-and-refetch,
    // not a per-row cache patch). Disabled/empty-types subscriptions no-op so the flag fully gates the
    // EventSource from ever opening.
    subscribeToQueueFeed: build.query<QueueFeedState, { types: QueueType[]; enabled: boolean; patching?: boolean }>({
      queryFn: () => ({ data: {} as QueueFeedState }),
      // Drop the entry as soon as the last subscriber unmounts so the SSE closes promptly instead of
      // lingering for the default 60s.
      keepUnusedDataFor: 0,
      async onCacheEntryAdded(arg, { cacheDataLoaded, cacheEntryRemoved, dispatch, updateCachedData }) {
        if (!arg.enabled || arg.types.length === 0) {
          return;
        }

        let eventSource: EventSource | null = null;
        let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
        let debounceTimer: ReturnType<typeof setTimeout> | null = null;
        let firstPendingAt: number | null = null;
        let pendingJitterMs = 0;
        let failedAttempts = 0;
        let stopped = false;
        let hasConnected = false;
        let sequence = 0;
        // Suppress refetches while the tab is backgrounded and resync once on return. Keyed on
        // visibilityState (not focus) so a visible-but-unfocused tab on a second monitor keeps
        // updating; only a genuinely hidden/minimized tab pauses.
        let hidden = document.visibilityState === 'hidden';
        let pendingWhileHidden = false;

        const scheduleInvalidate = (): void => {
          if (stopped) return;
          if (hidden) {
            // Nobody's looking — remember a change happened and refetch once when they return.
            pendingWhileHidden = true;
            return;
          }
          if (firstPendingAt === null) {
            firstPendingAt = Date.now();
            // Fix the jitter once per pending window so clients stay decorrelated even when the
            // max-wait cap forces the flush; re-rolling it per message would let a sustained burst
            // converge every client onto the same cap deadline and re-synchronize the spike.
            pendingJitterMs = Math.random() * QUEUE_FEED_INVALIDATE_JITTER_MS;
          }
          if (debounceTimer) {
            clearTimeout(debounceTimer);
          }
          const untilMaxWait = QUEUE_FEED_INVALIDATE_MAX_WAIT_MS + pendingJitterMs - (Date.now() - firstPendingAt);
          const delay = Math.min(QUEUE_FEED_INVALIDATE_DEBOUNCE_MS + pendingJitterMs, Math.max(0, untilMaxWait));
          debounceTimer = setTimeout(() => {
            debounceTimer = null;
            firstPendingAt = null;
            dispatch(queuesApi.util.invalidateTags([{ type: 'Queues', id: 'LIST' }]));
          }, delay);
        };

        const handleVisibilityChange = (): void => {
          if (stopped) return;
          if (document.visibilityState === 'hidden') {
            hidden = true;
            // A refetch already queued while visible must not fire into a hidden tab — cancel it and
            // defer to a single resync on return.
            if (debounceTimer) {
              clearTimeout(debounceTimer);
              debounceTimer = null;
              firstPendingAt = null;
              pendingWhileHidden = true;
            }
            return;
          }
          hidden = false;
          if (pendingWhileHidden) {
            pendingWhileHidden = false;
            scheduleInvalidate();
          }
        };

        const stop = (): void => {
          stopped = true;
          document.removeEventListener('visibilitychange', handleVisibilityChange);
          if (debounceTimer) clearTimeout(debounceTimer);
          if (reconnectTimer) clearTimeout(reconnectTimer);
          firstPendingAt = null;
          eventSource?.close();
          eventSource = null;
        };

        const connect = (url: string): void => {
          if (stopped) return;

          const source = new EventSource(url);
          eventSource = source;

          source.addEventListener('open', () => {
            failedAttempts = 0;
            // Every message that arrived while the stream was down is gone for good. Signal-and-
            // refetch tolerated that because the next refetch rebuilt everything anyway; patching
            // does not, so a reconnect must resync from scratch or the gap becomes permanent drift.
            //
            // Routed through scheduleInvalidate rather than dispatched directly so the resync is
            // jittered: the reconnect backoff is deterministic, so connections that drop together —
            // a deploy, or a cohort of tabs aging out of the backend timeout at once — would
            // otherwise all resync on the same instant and spike the API. It also inherits the
            // hidden-tab gate, which is free here because the drift already spanned the whole
            // disconnect; another 1.5s makes no difference.
            if (hasConnected) {
              scheduleInvalidate();
            }
            hasConnected = true;
          });

          source.addEventListener('message', (event: MessageEvent) => {
            let message: QueueFeedEvent | undefined;
            try {
              message = JSON.parse(event.data) as QueueFeedEvent;
            } catch {
              // A malformed payload is still a signal that something changed.
            }

            sequence += 1;
            const nextSequence = sequence;
            updateCachedData((draft) => {
              // Absent during a rolling deploy, when some pods do not attach it yet. Keep the last
              // known value; rendering 0 would look like an emptied queue.
              if (typeof message?.unfilteredTotal === 'number') {
                draft.unfilteredTotal = message.unfilteredTotal;
              }
              if (message) {
                draft.lastEvent = { ...message, sequence: nextSequence };
              }
            });

            // With patching on, the subscriber decides per event whether a refetch is warranted.
            // Invalidating here as well would refetch the whole list on every message and undo it.
            // A message that failed to parse never reaches the subscriber, so it has to fall back to
            // a refetch here or the change it signalled would be lost entirely.
            if (!arg.patching || !message) {
              scheduleInvalidate();
            }
          });

          source.addEventListener('error', () => {
            // Only take over reconnecting once the browser has genuinely given up (CLOSED) — see
            // createSSECacheHandler for why a CONNECTING error must be left alone.
            if (stopped || source.readyState !== EventSource.CLOSED) return;
            // A reconnect is already queued — without this guard, repeated CLOSED errors on the
            // same dead source would each schedule their own timer, and only the last one's
            // EventSource would ever be tracked, leaking the earlier ones.
            if (reconnectTimer) return;

            source.close();
            eventSource = null;
            failedAttempts += 1;
            const delay = Math.min(
              QUEUE_FEED_RECONNECT_BASE_DELAY_MS * 2 ** (failedAttempts - 1),
              QUEUE_FEED_RECONNECT_MAX_DELAY_MS,
            );
            reconnectTimer = setTimeout(() => {
              reconnectTimer = null;
              connect(url);
            }, delay);
          });
        };

        try {
          await cacheDataLoaded;
          // Re-read after the await: the tab can change visibility between the initial read above and
          // attaching the listener, and that transition fires no event we would otherwise catch.
          hidden = document.visibilityState === 'hidden';
          document.addEventListener('visibilitychange', handleVisibilityChange);
          connect(`/api/queues/feed?types=${arg.types.join(',')}`);
        } catch {
          // cacheDataLoaded rejected — entry removed before data loaded
        }

        await cacheEntryRemoved;
        stop();
      },
    }),
  }),
});

export const {
  useGetQueuesQuery,
  useUpdateQueueMutation,
  useLazyGetQueuesQuery,
  useAddNoteToQueueMutation,
  useGetQueueByAccountIdAndTypeQuery,
  useGetQueuesByAccountIdAndDocumentTypeQuery,
  useGetQueueListFiltersQuery,
  useGetQueueListFilterQuery,
  useAddFlagToQueueMutation,
  useUpdateFlagMutation,
  useResolveFlagMutation,
  useGetQueueByIdQuery,
  useLazyGetQueueByIdQuery,
  useGetQueueHoldMotionStatusQuery,
  useUpdateHoldMotionSettingsMutation,
  useGetHoldMotionProgramSettingsQuery,
  useUpdateHoldMotionProgramSettingMutation,
  useGetRequirementFlagSettingsForQueueQuery,
  useLazyCheckQueuesExportLimitQuery,
  useCreateQueuesExportRequestMutation,
  useSubscribeToQueueFeedQuery,
} = queuesApi;

export const useGetQueuesByAccountIdQuery = (
  args: { accountId: string; types?: QueueType[] },
  options?: { skip?: boolean; [key: string]: unknown },
) => {
  const { accountId, types } = args;
  return queuesApi.endpoints.getQueuesByAccountId.useQuery(
    { accountId },
    {
      ...options,
      skip: !accountId || options?.skip,
      selectFromResult: ({
        data,
        ...rest
      }: {
        data: QueueDocumentResponse[] | undefined;
        [key: string]: unknown;
      }) => {
        if (!data || !types?.length) {
          return { data, ...rest };
        }
        const typeSet = new Set(types);
        return {
          data: data.filter((q: QueueDocumentResponse) => typeSet.has(q.type)),
          ...rest,
        };
      },
    },
  );
};

export const useLazyGetQueuesByAccountIdQuery = () => {
  const [trigger, result] = queuesApi.endpoints.getQueuesByAccountId.useLazyQuery();

  const fetchQueues = useCallback(
    async ({ accountId, types }: { accountId: string; types?: QueueType[] }) => {
      const queues = await trigger({ accountId }).unwrap();

      if (!types?.length) {
        return queues;
      }

      const typeSet = new Set(types);

      return queues.filter((queue: QueueDocumentResponse) => typeSet.has(queue.type));
    },
    [trigger],
  );

  return [fetchQueues, result] as const;
};
